import type { Dispatch, SetStateAction } from "react"

import { useTranslation } from "react-i18next"
import type { UseMutationResult } from "react-query"
import { type Edge, type Node, useStoreApi } from "reactflow"

import * as Yup from "yup"
import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"
import type { FormikProps } from "formik"
import { t } from "i18next"

import type { CreateWorkflowRequest, UpdateWorkflowRequest } from "../../../../types/custom/projects"
import type { WorkflowConstantFeature } from "../../../../types/generated/api/WorkflowConstantFeature"
import { WorkflowCreateRequest } from "../../../../types/generated/api/WorkflowCreateRequest"
import type { WorkflowInputFeature } from "../../../../types/generated/api/WorkflowInputFeature"
import type { WorkflowList } from "../../../../types/generated/api/WorkflowList"
import type { WorkflowVersionRetrieve } from "../../../../types/generated/api/WorkflowVersionRetrieve"
import { PROHIBITED_REASONS_KEYWORDS, PROHIBITED_WORKFLOWS_KEYWORDS } from "../../../../utils/constants"
import { isInputValueValid } from "../../../../utils/genericHelpers"
import type { SchemaFeature } from "../../types/interfaces"
import { checkIfValuesMatchTypes, handleCreateWorkflow, handleUpdateWorkflow } from "../../utils/workflowHelpers"

interface WorkflowFormValues {
  workflowName: string
  computedFeatures: Array<{ uuid: string; value: string }>
  renderControl: number
  features: WorkflowInputFeature[]
  constants: WorkflowConstantFeature[]
  isSchemaSubmit: boolean
}

const validationSchema = Yup.object({
  workflowName: Yup.string().required(t("Workflows.validation.workflowNameRequired", { ns: "workflows" })),
})

interface UseWorkflowFormProps {
  query: { workflowName: string }
  nodes: Node[]
  isCreateMode: boolean
  isEditMode: boolean
  workflow?: { data?: { uuid: string; name: string } }
  projectId: string
  activeWorkflowVersion?: WorkflowVersionRetrieve
  orderedWorkflows: WorkflowList[]
  edges: Edge[]
  currentWorkflow: { workflowName: string; workflowId: string }
  createWorkflowMutation: UseMutationResult<AxiosResponse, AxiosError, CreateWorkflowRequest, unknown>
  updateWorkflowMutation: UseMutationResult<AxiosResponse, AxiosError, UpdateWorkflowRequest, unknown>
  setIsCreateMode: Dispatch<SetStateAction<boolean>>
  setIsEditMode: Dispatch<SetStateAction<boolean>>
  setIsDuplicated: Dispatch<SetStateAction<boolean>>
  setLastWorkflowUpdatePayload: Dispatch<SetStateAction<UpdateWorkflowRequest | undefined>>
  hasSchemaChanged: (newSchema: SchemaFeature[], oldSchema: SchemaFeature[]) => boolean
}

/**
 * Custom hook for managing workflow form state and submission logic.
 * Handles validation, node processing, and workflow creation/update operations.
 *
 * @param props - Configuration object containing workflow data and mutation handlers
 * @returns FormikProps instance for form management
 */
export const useWorkflowForm = ({
  query,
  nodes,
  isCreateMode,
  isEditMode,
  workflow,
  projectId,
  activeWorkflowVersion,
  orderedWorkflows,
  edges,
  currentWorkflow,
  createWorkflowMutation,
  updateWorkflowMutation,
  setIsCreateMode,
  setIsEditMode,
  setIsDuplicated,
  setLastWorkflowUpdatePayload,
  hasSchemaChanged,
}: UseWorkflowFormProps): FormikProps<WorkflowFormValues> => {
  // Translation hook
  const { t } = useTranslation()

  // React Flow hooks
  const store = useStoreApi()
  const { setNodes } = store.getState()

  return useFormik<WorkflowFormValues>({
    initialValues: {
      workflowName: query.workflowName,
      computedFeatures: [],
      renderControl: 1,
      features: [] as WorkflowInputFeature[],
      constants: [] as WorkflowConstantFeature[],
      isSchemaSubmit: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Data preparation and validation
      // Extracting non-empty features then trim each single one
      const validFeatures = values.features?.filter((feat) => feat?.name !== "")
      const validConstants = values.constants?.filter((constant) => constant?.name !== "")

      const trimmedWorkflowName = values.workflowName.trim()

      // Validate workflow name against prohibited keywords
      if (!isInputValueValid("workflow", trimmedWorkflowName, PROHIBITED_WORKFLOWS_KEYWORDS)) {
        return
      }

      // Trim feature and constant names
      const trimmedFeatures = validFeatures?.map((feat) => {
        return { ...feat, name: feat.name.trim() }
      })
      const trimmedConstants = validConstants?.map((constant) => {
        return { ...constant, name: constant.name.trim() }
      })

      // Validate constant values match their types
      const isNotValidConstant = checkIfValuesMatchTypes(trimmedConstants)

      if (isNotValidConstant) {
        return NotificationUtils.toast(t("Workflows.invalidConstantValue", { name: isNotValidConstant }), {
          severity: "error",
        })
      }

      // Node validation and processing
      const validatedNodes = []

      for (const node of nodes) {
        if (node.data.nodeType === "EndResultNode" && "description" in node.data) {
          // Validate label nodes reason field with prohibited BE keywords
          if (PROHIBITED_REASONS_KEYWORDS.some((keyword) => keyword === node.data.description.trim())) {
            NotificationUtils.toast(
              t("notifications.prohibitedLabelReason", {
                keywords: PROHIBITED_REASONS_KEYWORDS.join(" or "),
              }),
              {
                severity: "error",
              },
            )

            return
          }

          // Remove description field if it is empty
          if (node.data.description.trim() === "") {
            delete node.data.description
          }
        }

        validatedNodes.push(node)
      }

      setNodes(validatedNodes)

      // Workflow operations
      // Indicator for creating schema with empty workflow
      const isSchemaWithEmptyWorkflow =
        nodes?.length === 2 &&
        nodes[0]?.data?.nodeType === "StartNode" &&
        nodes[1]?.type === "AddBlockNode" &&
        isCreateMode &&
        (trimmedFeatures?.length > 0 || trimmedConstants?.length > 0)

      if (
        values.isSchemaSubmit &&
        workflow?.data?.uuid &&
        (hasSchemaChanged(
          trimmedFeatures as SchemaFeature[],
          activeWorkflowVersion?.schema?.features as SchemaFeature[],
        ) ||
          hasSchemaChanged(trimmedConstants, activeWorkflowVersion?.constants || []))
      ) {
        try {
          const updateWorkflowPayload = {
            name: trimmedWorkflowName,
            projectId: projectId as string,
            workflowUUID: workflow?.data?.uuid,
            schema: [...trimmedFeatures],
            constants: [...trimmedConstants],
          }
          setLastWorkflowUpdatePayload(updateWorkflowPayload)
          await updateWorkflowMutation.mutateAsync(updateWorkflowPayload)
        } catch (e) {
          console.warn(e)

          if (
            !(e as AxiosError<{ details: string }>).response?.data?.details?.includes("colliding with project schema")
          )
            resetForm()
        }
      } else if (isSchemaWithEmptyWorkflow && !workflow?.data?.uuid) {
        try {
          await createWorkflowMutation.mutateAsync({
            name: trimmedWorkflowName,
            state: WorkflowCreateRequest.state.DISABLED,
            projectId: projectId as string,
            schema: [...trimmedFeatures],
            constants: [...trimmedConstants],
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          if (e?.response?.data?.details) {
            NotificationUtils.toast(`${e?.response?.data?.details}`, {
              severity: "error",
            })
          } else if (e?.response?.data?.nodes?.length > 0) {
            NotificationUtils.toast(`${e?.response?.data?.nodes[0]}`, {
              severity: "error",
            })
          } else {
            NotificationUtils.toast(t("notifications.createWorkflow.error"), {
              severity: "error",
            })
          }
        }
      }

      if (isCreateMode && !isSchemaWithEmptyWorkflow) {
        const returnedNodes = await handleCreateWorkflow(
          trimmedWorkflowName,
          [...nodes],
          orderedWorkflows,
          createWorkflowMutation,
          projectId as string,
          setIsCreateMode,
          edges,
          [...trimmedFeatures],
          [...trimmedConstants],
          setIsDuplicated,
        )
        if (returnedNodes) {
          setNodes([...returnedNodes])
        }
      } else if (isEditMode && !values.isSchemaSubmit) {
        const returnedNodes = await handleUpdateWorkflow(
          projectId as string,
          currentWorkflow["workflowId"] || "",
          updateWorkflowMutation,
          setIsEditMode,
          trimmedWorkflowName,
          edges,
          orderedWorkflows,
          nodes,
          workflow?.data?.name || "",
          trimmedFeatures,
          trimmedConstants,
          setLastWorkflowUpdatePayload,
          activeWorkflowVersion,
        )
        if (returnedNodes) {
          setNodes([...returnedNodes])
        }
      }
    },
  })
}
